// src/pages/ChatBot/query-logs/derive.ts
//
// Client-side mirror of the backend's derived status/flags
// (queryLogStatus.ts / queryLogFlags.ts). List rows arrive with status and
// flags precomputed, but GET /query-logs/:id returns the raw doc — the
// detail modals derive here. This is also the only place `low-confidence`
// can be derived with full fidelity, because it needs the chunk trace.

import type {
  FeedbackSignal,
  QueryFlag,
  QueryFlagName,
  QueryLogDoc,
  QueryStatus,
  QueryStatusName,
} from './types';

// Mirror of the backend normalizer: numeric passes through, legacy
// 'positive'/'negative' strings map to 1/-1, pre-rating incorrect:true reads
// as -1. undefined = never rated. Raw detail docs need this; list projections
// arrive already normalized.
export function normalizeFeedbackSignal(
  doc: Pick<QueryLogDoc, 'feedback' | 'incorrect'>,
): FeedbackSignal | undefined {
  const { feedback } = doc;
  if (feedback === 1 || feedback === -1 || feedback === 0) return feedback;
  if (feedback === 'positive') return 1;
  if (feedback === 'negative') return -1;
  return doc.incorrect === true ? -1 : undefined;
}

export const QUERY_STATUSES: Record<QueryStatusName, QueryStatus> = {
  Answered: { id: 0, status: 'Answered' },
  Unanswered: { id: 1, status: 'Unanswered', warning: true },
  Incorrect: { id: 2, status: 'Incorrect', warning: true },
  Custom: { id: 3, status: 'Custom' },
  Error: { id: 4, status: 'Error', warning: true },
};

export const QUERY_FLAGS: Record<QueryFlagName, QueryFlag> = {
  'suspicious-query': { id: 0, flag: 'suspicious-query', warning: true },
  'suspicious-answer': { id: 1, flag: 'suspicious-answer', warning: true },
  'generation-error': { id: 2, flag: 'generation-error', warning: true },
  'fallback-served': { id: 3, flag: 'fallback-served', warning: true },
  'override-served': { id: 4, flag: 'override-served' },
  'eval-set': { id: 5, flag: 'eval-set' },
  'text-search-failed': { id: 6, flag: 'text-search-failed', warning: true },
  'low-confidence': { id: 7, flag: 'low-confidence', warning: true },
  'positive-feedback': { id: 8, flag: 'positive-feedback' },
  'negative-feedback': { id: 9, flag: 'negative-feedback', warning: true },
  'ungrounded-answer': { id: 10, flag: 'ungrounded-answer', warning: true },
};

// Matches the backend config: reranker is an llm-judge on a 0-100 scale.
export const LOW_CONFIDENCE_RERANK_SCORE = 30;

const ANSWERED_SERVED = new Set(['pipeline', 'admin-override', 'manager-fallback']);

// Precedence (highest first): Error > Custom > Incorrect > Answered > Unanswered.
export function deriveQueryStatus(doc: QueryLogDoc): QueryStatus {
  if (doc.error === true || doc.outcome.served === 'error' || doc.outcome.generation === 'failed') {
    return QUERY_STATUSES.Error;
  }
  if (doc.customAnswer != null) return QUERY_STATUSES.Custom;
  // A thumbs-up never changes status; only thumbs-down surfaces as Incorrect.
  if (normalizeFeedbackSignal(doc) === -1) return QUERY_STATUSES.Incorrect;
  if (ANSWERED_SERVED.has(doc.outcome.served)) return QUERY_STATUSES.Answered;
  return QUERY_STATUSES.Unanswered;
}

export function deriveQueryFlags(doc: QueryLogDoc): QueryFlag[] {
  const flags: QueryFlag[] = [];
  const { outcome, chunks } = doc;

  if (outcome.refusalReason === 'suspicious') flags.push(QUERY_FLAGS['suspicious-query']);
  if (outcome.generation === 'blocked') flags.push(QUERY_FLAGS['suspicious-answer']);
  if (outcome.generation === 'failed') flags.push(QUERY_FLAGS['generation-error']);
  // Advisory guardrail finding: the answer served without a single citation marker.
  if (outcome.detail === 'ungrounded-answer') flags.push(QUERY_FLAGS['ungrounded-answer']);
  if (outcome.served === 'manager-fallback' || outcome.served === 'still-learning') {
    flags.push(QUERY_FLAGS['fallback-served']);
  }
  if (outcome.served === 'admin-override') flags.push(QUERY_FLAGS['override-served']);
  if (doc.evalSet === true) flags.push(QUERY_FLAGS['eval-set']);
  if (doc.retrievalNotes?.textSearchFailed === true) flags.push(QUERY_FLAGS['text-search-failed']);

  // User thumbs surface as flags: 1 → positive-feedback, -1 → negative-feedback (0 / absent → none).
  const signal = normalizeFeedbackSignal(doc);
  if (signal === 1) flags.push(QUERY_FLAGS['positive-feedback']);
  if (signal === -1) flags.push(QUERY_FLAGS['negative-feedback']);

  if (chunks && chunks.length > 0) {
    const hasFinal = chunks.some((c) => c.rank.final !== undefined);
    const topReranked = chunks.find((c) => c.rank.rerank === 1);
    const weakTopRerank =
      topReranked?.score.rerank !== undefined &&
      topReranked.score.rerank < LOW_CONFIDENCE_RERANK_SCORE;
    if (!hasFinal || weakTopRerank) flags.push(QUERY_FLAGS['low-confidence']);
  }

  return flags;
}

export function formatDateTime(iso?: string): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}
